import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { AppConfig, Application, ApplicationType, ENVIRONMENT, Environment } from '@maia/config';
import { LocalStore } from '../store';

const SELECTED_APP_KEY: Readonly<string> = 'app_context_selected_app';

const isApplication = (value: string | null): value is ApplicationType =>
  value !== null && (Object.values(Application) as readonly string[]).includes(value);

@Injectable({ providedIn: 'root' })
export class AppContextService {
  private environment: Environment = inject(ENVIRONMENT);
  private localStore: LocalStore = inject(LocalStore);

  private _selectedApplication: WritableSignal<ApplicationType | null> = signal(null);

  public selectedApplication: Signal<ApplicationType | null> = this._selectedApplication.asReadonly();

  public appConfig: Signal<AppConfig | null> = computed(() => {
    const id: ApplicationType | null = this._selectedApplication();
    return id ? this.environment.apps[id] : null;
  });

  public apiUrl: Signal<string | null> = computed(() => this.appConfig()?.apiUrl ?? null);

  constructor() {
    const stored: string | null = this.localStore.get<string>(SELECTED_APP_KEY);
    if (isApplication(stored)) this._selectedApplication.set(stored);
  }

  public setSelectedApplication(application: ApplicationType): void {
    this._selectedApplication.set(application);
    this.localStore.set(SELECTED_APP_KEY, application);
  }

  public clear(): void {
    this._selectedApplication.set(null);
    this.localStore.remove(SELECTED_APP_KEY);
  }
}
